import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import chipLogo from './images/Elinnos-chip.png';
import { useAuth } from './AuthWrapper';
import ApiService from '../services/api';
import Logger from '../utils/logger';

/**
 * IDELoginCallback - Handles the IDE (VS Code fork) OAuth login flow
 * 
 * Flow:
 * 1. IDE calls POST /api/ide/authorize with PKCE challenge
 * 2. Backend returns auth_request_id and login_url
 * 3. IDE opens login_url in browser (this page with ?auth_request_id=...)
 * 4. User logs in (or is already logged in)
 * 5. This page calls POST /api/ide/authorize/complete
 * 6. Backend returns callback_url with auth code
 * 7. This page redirects to chipdesigner-ide://auth/callback?code=...
 * 8. IDE exchanges code for tokens via POST /api/ide/token
 */
function IDELoginCallback() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, login_required, completing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const completingRef = useRef(false); // Guard against duplicate auth completions

  useEffect(() => {
    const handleIDEAuth = async () => {
      try {
        // Get auth_request_id from URL
        const params = new URLSearchParams(window.location.search);
        const authRequestId = params.get('auth_request_id');

        if (!authRequestId) {
          setStatus('error');
          setErrorMessage('Missing auth_request_id parameter. Please try signing in again from the IDE.');
          return;
        }

        Logger.debug('IDE login callback', { authRequestId, hasUser: !!user, authLoading });

        // Wait for auth check to complete
        if (authLoading) {
          return;
        }

        // If user is not logged in, they need to log in first
        if (!user) {
          setStatus('login_required');
          return;
        }

        // Guard: prevent duplicate completions from React re-renders
        if (completingRef.current) {
          return;
        }
        completingRef.current = true;

        // User is logged in, complete the IDE auth flow
        setStatus('completing');

        const apiBaseUrl = ApiService.baseUrl;
        const response = await fetch(`${apiBaseUrl}/ide/authorize/complete`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ auth_request_id: authRequestId })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to complete IDE authorization: ${response.status}`);
        }

        const { callback_url } = await response.json();
        Logger.debug('IDE auth completed', { callback_url });

        // Redirect to the IDE via deep link
        setStatus('success');

        // Small delay to show success message, then redirect
        setTimeout(() => {
          // Try to trigger the deep link
          window.location.href = callback_url;
        }, 1000);

      } catch (error) {
        Logger.error('IDE auth callback error', { error });
        setStatus('error');
        setErrorMessage(error.message || 'An error occurred during IDE authentication.');
      }
    };

    handleIDEAuth();
  }, [user, authLoading]);

  // If login is required, redirect to dashboard login with return URL
  useEffect(() => {
    if (status === 'login_required') {
      // Redirect to dashboard login page with return URL
      const currentUrl = window.location.href;
      const loginUrl = `/dashboard?redirect=${encodeURIComponent(currentUrl)}`;
      
      // Use hash-based navigation for HashRouter (file:// protocol)
      if (window.location.protocol === 'file:') {
        window.location.hash = `#${loginUrl}`;
      } else {
        window.location.href = loginUrl;
      }
    }
  }, [status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={chipLogo} 
              alt="ChipDesigner.AI" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">ChipDesigner IDE</h1>
          <p className="text-gray-400">Sign in to connect your IDE</p>
        </div>

        {/* Status Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-8 shadow-2xl text-center">
          {(status === 'checking' || status === 'login_required') && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">
                {status === 'checking' ? 'Checking Authentication...' : 'Redirecting to Login...'}
              </h2>
              <p className="text-gray-400">
                {status === 'checking' 
                  ? 'Please wait while we verify your session.'
                  : 'You need to sign in to connect your IDE.'}
              </p>
            </>
          )}

          {status === 'completing' && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Authorizing IDE...</h2>
              <p className="text-gray-400">
                Completing the connection to ChipDesigner IDE.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">IDE Connected!</h2>
              <p className="text-gray-400 mb-4">
                Your ChipDesigner IDE is now connected to your account.
              </p>
              <p className="text-gray-300 font-medium mb-6">
                Returning to the IDE... You can close this window.
              </p>
              <button
                onClick={() => window.close()}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Close Window
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Connection Failed</h2>
              <p className="text-gray-400 mb-4">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.close()}
                  className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close Window
                </button>
              </div>
            </>
          )}
        </div>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Having trouble? Make sure ChipDesigner IDE is running and try again.
          </p>
        </div>
      </div>
    </div>
  );
}

export default IDELoginCallback;
